'use client';

import React from 'react';

import Link from 'next/link';
import { useSearchParams } from 'next/navigation';

import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation } from '@tanstack/react-query';
import { ChevronDown, Plus, X } from 'lucide-react';
import { useFieldArray, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { useMonitoring } from '@kit/monitoring/hooks';
import { useSignInWithPhone } from '@kit/supabase/hooks/use-sign-in-with-phone';
import { useSupabase } from '@kit/supabase/hooks/use-supabase';
import { useVerifyOtp } from '@kit/supabase/hooks/use-verify-otp';
import { useVerifyPhoneOtp } from '@kit/supabase/hooks/use-verify-phone-otp';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@kit/ui/form';
import { PhoneInput } from '@kit/ui/phone-input';
import { toast } from '@kit/ui/sonner';
import { Trans } from '@kit/ui/trans';

import { useCaptcha } from '../../captcha/client';
import { useLastAuthMethod } from '../../hooks/use-last-auth-method';
import {
  isNetworkBlip,
  resolveAuthErrorMessage,
} from '../../lib/auth-error-message';
import { isFullName } from '../../schemas/full-name.schema';
import { PasswordSignUpSchema } from '../../schemas/password-sign-up.schema';
import { PhoneSignUpSchema } from '../../schemas/phone-auth.schema';
import type { SignupCustomField } from '../../schemas/signup-custom-fields.schema';
import { associateCustomerWithTenantAction } from '../../server/associate-customer-action';
import { createChildAccountsAction } from '../../server/create-child-accounts-action';
import { completePhoneSignupProfileAction } from '../../server/complete-phone-signup-profile-action';
import { identifyAccountAction } from '../../server/identify-account-action';
import { prepareAndSendPhoneOtpAction } from '../../server/prepare-and-send-phone-otp';
import { signUpWithPasswordAction } from '../../server/sign-up-action';
import {
  SignupCustomFieldsInput,
  refineSignupFields,
  signupProfileSetupPath,
} from '../signup-custom-fields-input';
import { SignupEmailVerifyStep } from '../signup-email-verify-step';
import { SignupPhoneVerifyStep } from '../signup-phone-verify-step';
import { NativeAuthButton } from './native-auth-button';
import { NativeAuthShell } from './native-auth-shell';
import {
  isBiometricAvailable,
  storeBiometricCredentials,
} from './native-biometric-service';
import { NativeGroupedInput, NativeInputRow } from './native-grouped-input';
import { NativeOtpScreen } from './native-otp-screen';
import { readSessionFromCookies } from './native-supabase-client';
import { markBiometricEnrolled } from './use-biometric-token-sync';

const ASSURANCE_LEVEL_2 = 'aal2';
const MAX_CHILDREN = 5;

type ChildEntry = { firstName: string; lastName: string;
};
type AuthMode = 'phone' | 'email';

type AuthProviders = {
  password: boolean;
  phone: boolean;
  magicLink: boolean;
  otp: boolean;
};

// --- Phone sign-up schema (with children) ---
const PhoneSignUpWithChildrenSchema = PhoneSignUpSchema.extend({
  children: z
    .array(
      z.object({
        firstName: z.string().min(2).max(50),
        lastName: z.string().min(2).max(50),
      }),
    )
    .max(MAX_CHILDREN)
    .optional()
    .default([]),
});

type PhoneSignUpFormValues = z.infer<typeof PhoneSignUpWithChildrenSchema>;

// --- Email sign-up schema (with children) ---
// PasswordSignUpSchema uses superRefine so we can't extend it directly.
// Wrap it with an intersection to add the children field.
const EmailSignUpWithChildrenSchema = PasswordSignUpSchema.and(
  z.object({
    children: z
      .array(
        z.object({
          firstName: z.string().min(2).max(50),
          lastName: z.string().min(2).max(50),
        }),
      )
      .max(MAX_CHILDREN)
      .optional()
      .default([]),
  }),
);

type EmailSignUpFormValues = z.infer<typeof EmailSignUpWithChildrenSchema>;

export function NativeSignUpPage({
  logoUrl,
  accountName,
  primaryColor,
  secondaryColor,
  appBackgroundColor = '#FFFFFF',
  appTextColor = '#1C1C1C',
  signInPath,
  displayTermsCheckbox,
  displayChildrenSection = true,
  signupCustomFields = [],
  providers,
  tenantSlug,
}: {
  logoUrl?: string | null;
  accountName?: string;
  primaryColor: string;
  secondaryColor: string;
  appBackgroundColor?: string;
  appTextColor?: string;
  signInPath: string;
  displayTermsCheckbox?: boolean;
  displayChildrenSection?: boolean;
  signupCustomFields?: SignupCustomField[];
  captchaSiteKey?: string;
  providers?: AuthProviders;
  tenantSlug?: string;
}) {
  const hasPhone = providers?.phone !== false;
  const hasEmail = providers?.password !== false;
  const hasBothMethods = hasPhone && hasEmail;

  const { t } = useTranslation();
  const isDark = isDarkColor(appBackgroundColor);
  const inputClass = isDark
    ? 'w-full bg-transparent text-base text-white outline-none placeholder:text-white/40'
    : 'w-full bg-transparent text-base text-gray-900 outline-none placeholder:text-gray-400';
  const childInputClass = isDark
    ? 'w-full rounded-lg bg-white/10 px-3 py-2 text-sm text-white outline-none placeholder:text-white/40'
    : 'w-full rounded-lg bg-black/5 px-3 py-2 text-sm text-gray-900 outline-none placeholder:text-gray-400';

  const params = useSearchParams();
  const prefillEmail = params.get('email') ?? '';
  const prefillName = params.get('name') ?? '';
  // Use only the first word for the welcome banner ("Welcome back, Sharefa"),
  // but keep the full name in the form field so the FullNameSchema check passes.
  const prefillFirstName = prefillName.trim().split(/\s+/)[0] ?? '';
  // Show a helper hint under the name input when the prefilled name is a
  // single word (intake captured only one) so the user understands why the
  // submit button stays disabled until they add a last name.
  const prefillNeedsFullName = Boolean(prefillName) && !isFullName(prefillName);
  // Validate the format so an attacker cannot inject arbitrary banner text.
  const rawPhoneHint = params.get('phone_hint');
  const prefillPhoneHint =
    rawPhoneHint && /^•{3} •• \d{4}$/.test(rawPhoneHint) ? rawPhoneHint : null;
  // Pre-fill phone when the user arrived from the phone sign-in identify
  // flow (unknown or guest_upgrade phone). E.164 only — guards against
  // attacker-controlled query strings injecting UI text.
  const rawPhone = params.get('phone');
  const prefillPhone =
    rawPhone && /^\+\d{6,20}$/.test(rawPhone) ? rawPhone : '';
  // Masked email hint shown when the phone identify flow returned
  // guest_upgrade — tells the user which email is on file for that phone.
  // Format mirrors maskEmail() in identify-phone-action.ts.
  const rawEmailHint = params.get('email_hint');
  const prefillEmailHint =
    rawEmailHint && /^[a-z0-9]•••@[a-z0-9]•••\.[a-z]{2,24}$/.test(rawEmailHint)
      ? rawEmailHint
      : null;
  const supabase = useSupabase();
  const monitoring = useMonitoring();
  const captcha = useCaptcha();
  const { recordAuthMethod } = useLastAuthMethod();

  const signInWithPhone = useSignInWithPhone();
  const verifyOtp = useVerifyPhoneOtp();
  const verifyEmailOtp = useVerifyOtp();

  // Server-action variant that pre-merges current tenant_slug into
  // auth.users.raw_user_meta_data before triggering the OTP. Used whenever a
  // tenantSlug is in scope, so the Send-SMS hook can resolve branding.
  const prepareAndSendOtp = useMutation({
    mutationFn: prepareAndSendPhoneOtpAction,
  });

  const [emailSignUpLoading, setEmailSignUpLoading] = React.useState(false);
  const [emailOtpEmail, setEmailOtpEmail] = React.useState<string | null>(null);
  // Email-mismatch resolution (parity with the web EmailMismatchBanner): when
  // the phone matches a guest whose on-file email differs from the typed one,
  // we hold the masked hint + the submitted values so the resolve buttons can
  // replay the signup with emailConflictResolution set.
  const [emailMismatch, setEmailMismatch] = React.useState<{
    emailHint: string;
    values: EmailSignUpFormValues;
  } | null>(null);
  // Phone counterpart: EMAIL matched a guest whose on-file phone differs from
  // the typed one (uploaded guests — the on-file number is the tenant's
  // WhatsApp reach). Carries any prior email resolution so replays compose.
  const [phoneMismatch, setPhoneMismatch] = React.useState<{
    phoneHint: string;
    values: EmailSignUpFormValues;
    emailResolution?: 'use-existing' | 'override';
  } | null>(null);

  // If the user arrived with ?email= (email identify flow), default to email
  // mode. If they arrived with ?phone= (phone identify flow), default to
  // phone mode. Otherwise prefer phone when available.
  const initialMode: AuthMode = prefillEmail
    ? 'email'
    : prefillPhone || hasPhone
      ? 'phone'
      : 'email';
  const [mode, setMode] = React.useState<AuthMode>(initialMode);
  const [otpSent, setOtpSent] = React.useState(false);
  const [otpPhone, setOtpPhone] = React.useState('');

  // Claim flow (Phase C): a signup collided with an EXISTING account. Instead
  // of a dead-end toast, offer the recovery path through the credential the
  // account provably owns.
  //  - phone_exists: the typed phone belongs to a full account → in-place
  //    WhatsApp OTP signs them into it (claimVerifyRef re-routes the post-
  //    verify redirect to /profile?fix=email when their email is unverified).
  //  - email_exists: the typed email belongs to an account. canUpgrade=true
  //    means it's a passwordless guest → continue via email-mode sign-up
  //    (the guest-upgrade branch claims it); false → sign in with that email.
  //  - user_exists: both credentials matched — they already have this account.
  const [claim, setClaim] = React.useState<
    | { kind: 'phone_exists'; phone: string }
    | { kind: 'email_exists'; email: string; canUpgrade: boolean }
    | { kind: 'user_exists' }
    | null
  >(null);
  const claimVerifyRef = React.useRef(false);
  const [pendingChildren, setPendingChildren] = React.useState<ChildEntry[]>([]);
  const phoneVerifiedRef = React.useRef(false);
  const [phoneProfilePending, startPhoneProfileTransition] = React.useTransition();
  // Optional email typed on the phone-mode form. After the phone OTP verifies
  // we offer a SKIPPABLE code step for it (it was stored unverified).
  const [pendingEmail, setPendingEmail] = React.useState<string | null>(null);
  const [showEmailVerify, setShowEmailVerify] = React.useState(false);
  // Email mode: after the email OTP verifies, offer the skippable WhatsApp
  // verify step for the optional phone typed on the form (self-skipping when
  // no phone was stored / it's already confirmed — e.g. guest upgrade).
  const [showPhoneVerify, setShowPhoneVerify] = React.useState(false);
  const [resendCooldown, setResendCooldown] = React.useState(0);
  const [childrenOpenPhone, setChildrenOpenPhone] = React.useState(false);
  const [childrenOpenEmail, setChildrenOpenEmail] = React.useState(false);

  React.useEffect(() => {
    if (resendCooldown <= 0) return;
    const timer = setInterval(() => {
      setResendCooldown((prev) => prev - 1);
    }, 1000);
    return () => clearInterval(timer);
  }, [resendCooldown]);

  const phoneSchema = PhoneSignUpWithChildrenSchema.superRefine(
    refineSignupFields(signupCustomFields),
  );
  const emailSchema = EmailSignUpWithChildrenSchema.superRefine(
    refineSignupFields(signupCustomFields),
  );

  // --- Phone form ---
  const phoneForm = useForm<PhoneSignUpFormValues>({
    resolver: zodResolver(phoneSchema),
    mode: 'onChange',
    defaultValues: {
      name: prefillName,
      email: prefillEmail,
      phone: prefillPhone,
      children: [],
    },
  });

  const phoneChildren = useFieldArray({
    control: phoneForm.control,
    name: 'children',
  });

  // --- Email form ---
  const emailForm = useForm<EmailSignUpFormValues>({
    resolver: zodResolver(emailSchema),
    mode: 'onChange',
    defaultValues: {
      name: prefillName,
      email: prefillEmail,
      password: '',
      repeatPassword: '',
      phoneNumber: '',
      children: [],
    },
  });

  const emailChildren = useFieldArray({
    control: emailForm.control,
    name: 'children',
  });


  const enrollBiometric = React.useCallback(async () => {
    try {
      const available = await isBiometricAvailable();
      if (!available) return;

      const session = readSessionFromCookies();
      if (!session?.refresh_token || !session?.user?.id) return;

      await storeBiometricCredentials(session.user.id, session.refresh_token);
      markBiometricEnrolled();
    } catch {
      // Non-critical
    }
  }, []);

  // --- Redirect helper ---
  // Resolves the post-auth destination. This page is sign-up only, so defaulting to
  // profile-setup is correct — new users should always complete their profile.
  // The profile-setup page itself redirects to '/' immediately if there are no fields
  // to fill, so this is safe even if the tenant has no signup custom fields.
  const redirectAfterAuth = React.useCallback(
    async (inviteToken?: string | null) => {
      const next = signupProfileSetupPath(params.get('next'));

      try {
        const assurance =
          await supabase.auth.mfa.getAuthenticatorAssuranceLevel();

        if (assurance.error) {
          window.location.href = next;
          return;
        }

        const { nextLevel, currentLevel } = assurance.data;

        if (nextLevel === ASSURANCE_LEVEL_2 && nextLevel !== currentLevel) {
          const nextPath = inviteToken
            ? `/join?invite_token=${inviteToken}&next=${encodeURIComponent(next)}`
            : next;
          window.location.href = `/auth/verify?next=${encodeURIComponent(nextPath)}`;
          return;
        }

        if (inviteToken) {
          window.location.href = `/join?${new URLSearchParams({ invite_token: inviteToken, next }).toString()}`;
        } else {
          window.location.href = next;
        }
      } catch {
        window.location.href = next;
      }
    },
    [supabase, params],
  );

  // --- Phone: send OTP ---
  const handlePhoneSendOtp = async (data: PhoneSignUpFormValues) => {
    phoneVerifiedRef.current = false;
    // C4 pre-check: an email that already belongs to an account would make
    // the OTP send explode later inside kit.setup_new_user (raw unique-
    // violation). Catch it up front and offer the claim path instead.
    // Identify failures never block the signup (fail open to the old path).
    if (data.email) {
      try {
        const identified = await identifyAccountAction({ email: data.email });

        if (identified.kind !== 'unknown') {
          setClaim({
            kind: 'email_exists',
            email: data.email,
            canUpgrade: identified.kind === 'guest_upgrade',
          });
          return;
        }
      } catch {
        // fall through to the normal send
      }
    }

    try {
      if (tenantSlug) {
        const result = await prepareAndSendOtp.mutateAsync({
          phone: data.phone,
          tenantSlug,
          captchaToken: captcha.token,
          shouldCreateUser: true,
          name: data.name,
          email: data.email || undefined,
        });

        if (!result.success) {
          throw new Error(result.error);
        }
      } else {
        await signInWithPhone.mutateAsync({
          phone: data.phone,
          captchaToken: captcha.token,
          shouldCreateUser: true,
          name: data.name,
          email: data.email || undefined,
        });
      }
      captcha.reset();
      setOtpPhone(data.phone);
      if (data.children && data.children.length > 0) {
        setPendingChildren(data.children);
      }
      setPendingEmail(data.email || null);
      setOtpSent(true);
      setResendCooldown(30);
    } catch (error) {
      captcha.reset();
      handleSignUpError(error);
    }
  };

  // --- Phone: verify OTP ---
  const handleVerifyOtp = async (token: string) => {
    try {
      if (!phoneVerifiedRef.current) {
        await verifyOtp.mutateAsync({
        phone: otpPhone,
        token,
        type: 'sms' });
        phoneVerifiedRef.current = true;
      }
      recordAuthMethod('otp');
      if (!claimVerifyRef.current) {
        const values = phoneForm.getValues();
        const profile = await completePhoneSignupProfileAction({
          name: values.name,
          email: values.email,
          signupFields: values.signupFields,
          tenantSlug,
        });
        if (!profile.success)
          throw new Error(
            'error' in profile ? profile.error : 'Unable to save your profile',
          );
      }

      associateCustomerWithTenantAction().catch((err) => {
        monitoring.captureException(
          err instanceof Error ? err : new Error('Tenant association failed'),
        );
      });
      if (pendingChildren.length > 0) {
        createChildAccountsAction({ children: pendingChildren }).catch(
          (err) => {
            monitoring.captureException(
              err instanceof Error
                ? err
                : new Error('Child account creation failed'),
            );
          },
        );
      }
      await enrollBiometric();

      // Claim flow: the OTP just signed them into their EXISTING account.
      // If its email is unverified (the typo population), land directly on
      // the profile repair dialog instead of the new-user route.
      if (claimVerifyRef.current) {
        claimVerifyRef.current = false;

        try {
          // getUser(): needs email_confirmed_at, not in JWT claims
          const { data: authData } = await supabase.auth.getUser();

          if (authData.user && !authData.user.email_confirmed_at) {
            window.location.href = '/profile?fix=email';
            return;
          }
        } catch {
          // fall through to the standard redirect
        }
      }

      // New signup with an optional email: offer the skippable verify step
      // before leaving the auth flow (the email was stored unverified).
      if (pendingEmail) {
        setShowEmailVerify(true);
        return;
      }

      await redirectAfterAuth(params.get('invite_token'));
    } catch (error) {
      monitoring.captureException(
        error instanceof Error ? error : new Error('OTP verify failed'),
      );
      toast.error(t('auth:errors.generic'));
    }
  };

  const handleResendOtp = () => {
    setOtpSent(false);
    setResendCooldown(0);
    // Leaving the OTP screen abandons any in-flight claim.
    claimVerifyRef.current = false;
  };

  // --- Claim (phone_exists): sign into the existing account in place ---
  const handleClaimPhoneOtp = async () => {
    if (claim?.kind !== 'phone_exists') return;

    const phone = claim.phone;

    try {
      if (tenantSlug) {
        const result = await prepareAndSendOtp.mutateAsync({
          phone,
          tenantSlug,
          captchaToken: captcha.token,
          shouldCreateUser: false,
        });

        if (!result.success) {
          throw new Error(result.error);
        }
      } else {
        await signInWithPhone.mutateAsync({
          phone,
          captchaToken: captcha.token,
          shouldCreateUser: false,
        });
      }

      captcha.reset();
      claimVerifyRef.current = true;
      setClaim(null);
      // Claim signs into an EXISTING account — its email situation is handled
      // by the claimVerifyRef redirect, not the new-signup verify step.
      setPendingEmail(null);
      setOtpPhone(phone);
      setOtpSent(true);
      setResendCooldown(30);
    } catch (error) {
      captcha.reset();
      monitoring.captureException(
        error instanceof Error ? error : new Error('Claim OTP send failed'),
      );
      toast.error(resolveAuthErrorMessage(error, t));
    }
  };

  // --- Claim (email_exists, guest): continue via email-mode sign-up so the
  // guest-upgrade branch claims the existing profile ---
  const handleClaimContinueWithEmail = () => {
    if (claim?.kind !== 'email_exists') return;

    emailForm.setValue('email', claim.email, { shouldValidate: true });
    emailForm.setValue('name', phoneForm.getValues('name'));
    emailForm.setValue('phoneNumber', phoneForm.getValues('phone'));
    setClaim(null);
    setMode('email');
  };

  // --- Email: sign up (uses server action like web flow) ---
  // `resolution` is set when replaying the submit after the email-mismatch
  // banner; `phoneResolution` after the phone-mismatch banner ('use-existing'
  // keeps the on-file value, 'override' keeps the typed one) — full parity
  // with the web banners.
  const handleEmailSignUp = async (
    data: EmailSignUpFormValues,
    resolution?: 'use-existing' | 'override',
    phoneResolution?: 'use-existing' | 'override',
  ) => {
    setEmailSignUpLoading(true);
    // Clear ALL outcome state up front — a resubmit with edited values must
    // not stack a new banner next to a stale one (web-hook parity).
    setEmailMismatch(null);
    setPhoneMismatch(null);

    try {
      const result = await signUpWithPasswordAction({
        name: data.name,
        email: data.email,
        password: data.password,
        phoneNumber: data.phoneNumber,
        children: data.children,
        signupFields: data.signupFields,
        emailConflictResolution: resolution,
        phoneConflictResolution: phoneResolution,
      });

      captcha.reset();

      if (!result.success) {
        // Branch on STRUCTURED CODES (not English-string matching). Each code
        // maps to a deliberate UX — mirrors the web container.
        if ('code' in result) {
          if (result.code === 'email_mismatch') {
            // Show the resolve banner (Use-on-file / Use-new / Cancel) instead
            // of a dead-end toast. Stash the submitted values so a resolution
            // can replay the same submit with emailConflictResolution set.
            setEmailMismatch({ emailHint: result.emailHint, values: data });
            return;
          }

          if (result.code === 'phone_mismatch') {
            // Same banner-and-replay shape for the phone: keep the on-file
            // (often WhatsApp-proven) number or overwrite with the typed one.
            setPhoneMismatch({
              phoneHint: result.phoneHint,
              values: data,
              emailResolution: resolution,
            });
            return;
          }

          if (result.code === 'phone_exists') {
            // Claim flow instead of a dead-end toast: the typed phone belongs
            // to an existing account — verify it via WhatsApp OTP to sign in.
            if (data.phoneNumber) {
              setClaim({ kind: 'phone_exists', phone: data.phoneNumber });
            } else {
              // Plain variant: toasts render i18n link tags literally.
              toast.error(
                t(
                  'auth:errors.phoneAlreadyRegisteredPlain',
                  'This phone number is already registered — sign in instead.',
                ),
              );
            }
            return;
          }

          if (result.code === 'email_exists') {
            setClaim({
              kind: 'email_exists',
              email: data.email,
              canUpgrade: false,
            });
            return;
          }

          if (result.code === 'user_exists') {
            setClaim({ kind: 'user_exists' });
            return;
          }
        }

        // Genuine infra failure ({ success: false; error }).
        const errorMessage = 'error' in result ? result.error : undefined;
        throw new Error(errorMessage || 'Account creation failed');
      }

      setEmailMismatch(null);
      setPhoneMismatch(null);
      recordAuthMethod('password', { email: data.email });

      // Show email OTP verification screen (same as web flow).
      // The server action already sent the OTP email.
      setEmailOtpEmail(data.email);
    } catch (error) {
      captcha.reset();
      handleSignUpError(error);
    } finally {
      setEmailSignUpLoading(false);
    }
  };

  // Replay the signup with the user's email-mismatch resolution (web parity).
  const resolveEmailMismatch = (resolution: 'use-existing' | 'override') => {
    if (!emailMismatch) return;
    const values = emailMismatch.values;
    setEmailMismatch(null);
    void handleEmailSignUp(values, resolution);
  };

  // Replay with the phone resolution, preserving any earlier email resolution.
  const resolvePhoneMismatch = (resolution: 'use-existing' | 'override') => {
    if (!phoneMismatch) return;
    const { values, emailResolution } = phoneMismatch;
    setPhoneMismatch(null);
    void handleEmailSignUp(values, emailResolution, resolution);
  };

  // --- Email: verify OTP after email signup ---
  const handleVerifyEmailOtp = async (token: string) => {
    if (!emailOtpEmail) return;

    try {
      // Use type: 'recovery' to match the OTP minted by sendSignupOtpEmail
      // (admin.generateLink({ type: 'recovery' })). Verifying with 'recovery'
      // confirms the email and establishes a session.
      await verifyEmailOtp.mutateAsync({
        type: 'recovery',
        email: emailOtpEmail,
        token,
      });

      recordAuthMethod('password', { email: emailOtpEmail });

      // Now the session is established — safe to call server actions
      associateCustomerWithTenantAction().catch((err) => {
        monitoring.captureException(
          err instanceof Error ? err : new Error('Tenant association failed'),
        );
      });

      await enrollBiometric();

      // Skippable WhatsApp verify before leaving the auth flow — the step
      // self-skips when the account has no phone or it's already confirmed.
      setShowPhoneVerify(true);
    } catch (error) {
      monitoring.captureException(
        error instanceof Error ? error : new Error('Email OTP verify failed'),
      );
      // Prefer a specific reason (e.g. "link expired"); fall back to the
      // OTP-specific "code didn't work" message rather than the generic one.
      toast.error(resolveAuthErrorMessage(error, t, 'auth:errors.otpInvalid'));
    }
  };

  // --- Shared error handler (fallback only) ---
  // The "already exists" outcomes are now handled inline via structured codes
  // (email_mismatch / phone_exists / user_exists) — this handler only catches
  // GENUINELY thrown errors: infra failures and DB-level duplicate-key errors
  // raised by triggers (which still surface as thrown messages, not codes).
  const handleSignUpError = (error: unknown) => {
    const msg = error instanceof Error ? error.message : String(error ?? '');

    // 'User already registered' is thrown by the guest-upgrade branch when
    // GoTrue rejects the credential write as a duplicate — same meaning as
    // the DB-level patterns, so it gets the same actionable toast.
    const isDuplicateEmail =
      msg.includes('duplicate key value') ||
      msg.includes('accounts_email_key') ||
      msg.includes('already exists') ||
      msg.includes('already registered') ||
      msg.includes('Database error saving new user');

    if (isDuplicateEmail) {
      toast.error(t('auth:errors.duplicateEmailError'));
      return;
    }

    if (!isNetworkBlip(error)) {
      monitoring.captureException(
        error instanceof Error ? error : new Error(msg),
      );
    }

    // Signup-specific fallback: the user was creating an account, not
    // authenticating — errors.generic ("we weren't able to authenticate
    // you") is sign-in copy and reads as a wrong-password problem here.
    toast.error(t('auth:errors.accountCreationFailed'));
  };

  const isLoading =
    phoneProfilePending ||
    signInWithPhone.isPending ||
    prepareAndSendOtp.isPending ||
    emailSignUpLoading ||
    verifyOtp.isPending ||
    verifyEmailOtp.isPending;

  // --- Skippable WhatsApp verify (email mode, optional phone stored) ---
  if (showPhoneVerify) {
    return (
      <NativeAuthShell
        logoUrl={logoUrl}
        accountName={accountName}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        appBackgroundColor={appBackgroundColor}
        appTextColor={appTextColor}
      >
        <SignupPhoneVerifyStep
          tenantSlug={tenantSlug}
          onDone={() => redirectAfterAuth(params.get('invite_token'))}
        />
      </NativeAuthShell>
    );
  }

  // --- Skippable email verify (phone mode, optional email stored) ---
  // Checked BEFORE the otpSent branch: otpSent stays true underneath.
  if (showEmailVerify && pendingEmail) {
    return (
      <NativeAuthShell
        logoUrl={logoUrl}
        accountName={accountName}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        appBackgroundColor={appBackgroundColor}
        appTextColor={appTextColor}
      >
        <SignupEmailVerifyStep
          email={pendingEmail}
          onDone={() => redirectAfterAuth(params.get('invite_token'))}
        />
      </NativeAuthShell>
    );
  }

  // --- OTP screen (phone mode) ---
  if (otpSent) {
    return (
      <NativeAuthShell
        logoUrl={logoUrl}
        accountName={accountName}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        appBackgroundColor={appBackgroundColor}
        appTextColor={appTextColor}
        showBackButton
      >
        <NativeOtpScreen
          phone={otpPhone}
          loading={verifyOtp.isPending || phoneProfilePending}
          resendCooldown={resendCooldown}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          appBackgroundColor={appBackgroundColor}
          appTextColor={appTextColor}
          onVerify={(token) =>
            startPhoneProfileTransition(() => handleVerifyOtp(token))
          }
          onResend={handleResendOtp}
          onBack={handleResendOtp}
        />
      </NativeAuthShell>
    );
  }

  // --- OTP screen (email mode) ---
  if (emailOtpEmail) {
    return (
      <NativeAuthShell
        logoUrl={logoUrl}
        accountName={accountName}
        primaryColor={primaryColor}
        secondaryColor={secondaryColor}
        appBackgroundColor={appBackgroundColor}
        appTextColor={appTextColor}
        showBackButton
      >
        <NativeOtpScreen
          email={emailOtpEmail}
          loading={verifyEmailOtp.isPending}
          resendCooldown={resendCooldown}
          primaryColor={primaryColor}
          secondaryColor={secondaryColor}
          appBackgroundColor={appBackgroundColor}
          appTextColor={appTextColor}
          onVerify={handleVerifyEmailOtp}
          onResend={() => setEmailOtpEmail(null)}
          onBack={() => setEmailOtpEmail(null)}
        />
      </NativeAuthShell>
    );
  }

  return (
    <NativeAuthShell
      logoUrl={logoUrl}
      accountName={accountName}
      primaryColor={primaryColor}
      secondaryColor={secondaryColor}
      showBackButton
    >
      <div
        className="flex flex-col gap-5"
        style={{ maxHeight: 'calc(100dvh - 200px)', overflowY: 'auto' }}
      >
        {/* ========== Claim banner (Phase C) — the signup collided with an
            existing account; offer the recovery path instead of a dead end ==== */}
        {claim && (
          <div
            className="rounded-lg p-4 text-sm"
            style={{
              backgroundColor: `${appTextColor}10`,
              border: `1px solid ${appTextColor}40`,
              color: appTextColor,
            }}
            data-test="signup-claim-banner"
          >
            <p className="font-medium">
              {claim.kind === 'phone_exists' ? (
                <Trans
                  i18nKey="auth:claimPhoneHeading"
                  defaults="This phone number already has an account"
                />
              ) : claim.kind === 'email_exists' ? (
                <Trans
                  i18nKey="auth:claimEmailHeading"
                  defaults="This email already has an account"
                />
              ) : (
                <Trans
                  i18nKey="auth:claimAccountHeading"
                  defaults="You already have an account"
                />
              )}
            </p>

            <p className="mt-1" style={{ color: `${appTextColor}99` }}>
              {claim.kind === 'phone_exists' ? (
                <Trans
                  i18nKey="auth:claimPhoneBody"
                  defaults="We can send a code to your WhatsApp to sign you in — you can fix your details afterwards."
                />
              ) : claim.kind === 'email_exists' && claim.canUpgrade ? (
                <Trans
                  i18nKey="auth:claimEmailUpgradeBody"
                  defaults="We found an existing profile with this email. Continue with email sign-up to claim it."
                />
              ) : claim.kind === 'email_exists' ? (
                <Trans
                  i18nKey="auth:claimEmailBody"
                  defaults="Sign in with this email to get back into your account."
                />
              ) : (
                <Trans
                  i18nKey="auth:claimAccountBody"
                  defaults="Both this phone and email belong to your existing account — just sign in."
                />
              )}
            </p>

            <div className="mt-3 flex flex-col gap-2">
              {claim.kind === 'phone_exists' ? (
                <NativeAuthButton
                  type="button"
                  disabled={isLoading}
                  loading={prepareAndSendOtp.isPending || signInWithPhone.isPending}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  appTextColor={appTextColor}
                  onClick={handleClaimPhoneOtp}
                  data-test="claim-phone-send-otp"
                >
                  <Trans
                    i18nKey="auth:claimPhoneCta"
                    defaults="Send code on WhatsApp"
                  />
                </NativeAuthButton>
              ) : claim.kind === 'email_exists' && claim.canUpgrade ? (
                <NativeAuthButton
                  type="button"
                  disabled={isLoading}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  appTextColor={appTextColor}
                  onClick={handleClaimContinueWithEmail}
                >
                  <Trans
                    i18nKey="auth:claimEmailContinueCta"
                    defaults="Continue with email sign-up"
                  />
                </NativeAuthButton>
              ) : (
                <NativeAuthButton
                  type="button"
                  disabled={isLoading}
                  primaryColor={primaryColor}
                  secondaryColor={secondaryColor}
                  appTextColor={appTextColor}
                  onClick={() => {
                    const email =
                      claim.kind === 'email_exists' ? claim.email : '';
                    window.location.href = email
                      ? `${signInPath}?email=${encodeURIComponent(email)}`
                      : signInPath;
                  }}
                  data-test="claim-go-to-sign-in"
                >
                  <Trans
                    i18nKey="auth:claimSignInCta"
                    defaults="Go to sign in"
                  />
                </NativeAuthButton>
              )}

              <button
                type="button"
                disabled={isLoading}
                onClick={() => setClaim(null)}
                className="flex min-h-[44px] items-center justify-center text-sm font-medium disabled:opacity-50"
                style={{ color: `${appTextColor}99` }}
              >
                <Trans i18nKey="common:cancel" defaults="Cancel" />
              </button>
            </div>
          </div>
        )}

        {/* ========== Phone sign-up form ========== */}
        {hasPhone && mode === 'phone' && (
          <Form {...phoneForm}>
            <form
              onSubmit={phoneForm.handleSubmit(handlePhoneSendOtp)}
              className="flex flex-col gap-5"
            >
              {/* Guest-upgrade welcome banner — only shown when arriving */}
              {/* from the phone identify flow with a masked email on file. */}
              {prefillEmailHint && (
                <div
                  className="rounded-lg p-3 text-sm"
                  style={{
                    backgroundColor: `${appTextColor}10`,
                    border: `1px solid ${appTextColor}40`,
                    color: appTextColor,
                  }}
                >
                  <p className="font-medium">
                    {prefillFirstName ? (
                      <Trans
                        i18nKey="auth:welcomeBackNamed"
                        values={{ name: prefillFirstName }}
                        defaults="Welcome back, {{name}}"
                      />
                    ) : (
                      <Trans i18nKey="auth:welcomeBack" defaults="Welcome back" />
                    )}
                  </p>
                  <p className="mt-1" style={{ color: `${appTextColor}99` }}>
                    <Trans
                      i18nKey="auth:welcomeBackEmailHint"
                      values={{ emailHint: prefillEmailHint }}
                      components={{
                        strong: <strong style={{ color: appTextColor }} />,
                      }}
                      defaults="We have <strong>{{emailHint}}</strong> on file for this phone — use that email below to keep your existing data."
                    />
                  </p>
                </div>
              )}

              <NativeGroupedInput appBackgroundColor={appBackgroundColor}>
                <FormField
                  control={phoneForm.control}
                  name="name"
                  render={({ field, fieldState }) => {
                    const showSingleWordHint =
                      prefillNeedsFullName &&
                      Boolean(field.value) &&
                      !isFullName(field.value) &&
                      !fieldState.error;
                    return (
                      <FormItem>
                        <FormControl>
                          <NativeInputRow appTextColor={appTextColor}>
                            <input
                              type="text"
                              autoComplete="name"
                              placeholder={t('auth:namePlaceholder')}
                              disabled={isLoading}
                              className={inputClass}
                              data-test="native-name-input"
                              {...field}
                            />
                          </NativeInputRow>
                        </FormControl>
                        <FormMessage className="px-1" />
                        {showSingleWordHint && (
                          <p className="text-destructive px-1 text-xs">
                            <Trans i18nKey="auth:nameMustBeFullName" />
                          </p>
                        )}
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={phoneForm.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <NativeInputRow appTextColor={appTextColor}>
                          <PhoneInput
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={isLoading}
                            autoComplete="tel"
                            placeholder={t('auth:phoneNumberFormat')}
                            className={`native-phone-input border-0 bg-transparent text-base shadow-none focus-visible:ring-0 ${isDark ? 'text-white placeholder:text-white/40' : 'text-gray-900 placeholder:text-gray-400'}`}
                            data-test="native-signup-phone-input"
                          />
                        </NativeInputRow>
                      </FormControl>
                      <FormMessage className="px-1 text-red-400" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={phoneForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <NativeInputRow appTextColor={appTextColor}>
                          <input
                            type="email"
                            autoComplete="email"
                            placeholder={t('common:emailAddress', 'Email')}
                            disabled={isLoading}
                            className={inputClass}
                            data-test="native-signup-email-input"
                            {...field}
                          />
                        </NativeInputRow>
                      </FormControl>
                      <FormMessage className="px-1 text-red-400" />
                    </FormItem>
                  )}
                />
                {signupCustomFields.some((field) => field.for_parent) && (
                  <div className="space-y-3 p-3">
                    <SignupCustomFieldsInput
                      fields={signupCustomFields}
                      disabled={isLoading}
                    />
                  </div>
                )}
              </NativeGroupedInput>

              {displayChildrenSection && (
                <ChildrenSection
                  fields={phoneChildren.fields}
                  append={phoneChildren.append}
                  remove={phoneChildren.remove}
                  control={phoneForm.control}
                  isOpen={childrenOpenPhone}
                  setIsOpen={setChildrenOpenPhone}
                  primaryColor={primaryColor}
                  appTextColor={appTextColor}
                  isDark={isDark}
                  childInputClass={childInputClass}
                />
              )}

              {captcha.field}

              <NativeAuthButton
                type="submit"
                disabled={isLoading || !phoneForm.formState.isValid}
                loading={
                  signInWithPhone.isPending || prepareAndSendOtp.isPending
                }
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                appTextColor={appTextColor}
              >
                <Trans i18nKey="auth:signUpHeading" />
              </NativeAuthButton>

              {hasBothMethods && (
                <button
                  type="button"
                  onClick={() => {
                    setEmailMismatch(null);
                    setMode('email');
                  }}
                  className="flex min-h-[44px] items-center justify-center text-sm font-medium"
                  style={{ color: appTextColor }}
                >
                  <Trans i18nKey="auth:useEmailInstead" />
                </button>
              )}

              <TermsInlineNotice
                display={displayTermsCheckbox}
                appTextColor={appTextColor}
              />
            </form>
          </Form>
        )}

        {/* ========== Email sign-up form ========== */}
        {hasEmail && mode === 'email' && (
          <>
            {/* Email-mismatch resolution banner (parity with web). Phone matched
                a guest whose on-file email differs from the typed one; let the
                user pick which email to use and replay the submit. */}
            {emailMismatch && (
              <div
                className="rounded-lg p-4 text-sm"
                style={{
                  backgroundColor: `${appTextColor}10`,
                  border: `1px solid ${appTextColor}40`,
                  color: appTextColor,
                }}
              >
                <p className="font-medium">
                  <Trans i18nKey="auth:emailMismatchHeading" defaults="Is this you?" />
                </p>
                <p className="mt-1" style={{ color: `${appTextColor}99` }}>
                  <Trans
                    i18nKey="auth:emailMismatchBody"
                    values={{
                      emailHint: emailMismatch.emailHint,
                      typedEmail: emailMismatch.values.email,
                    }}
                    components={{ strong: <strong style={{ color: appTextColor }} /> }}
                    defaults="We already have a profile on this phone number linked to <strong>{{emailHint}}</strong>. You typed <strong>{{typedEmail}}</strong>. Which email do you want to use?"
                  />
                </p>
                <div className="mt-3 flex flex-col gap-2">
                  <NativeAuthButton
                    type="button"
                    disabled={emailSignUpLoading}
                    loading={emailSignUpLoading}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    appTextColor={appTextColor}
                    onClick={() => resolveEmailMismatch('use-existing')}
                  >
                    <Trans
                      i18nKey="auth:emailMismatchUseExisting"
                      defaults="Use the email on file"
                    />
                  </NativeAuthButton>
                  <button
                    type="button"
                    disabled={emailSignUpLoading}
                    onClick={() => resolveEmailMismatch('override')}
                    className="flex min-h-[44px] items-center justify-center rounded-xl px-4 text-sm font-medium disabled:opacity-50"
                    style={{
                      border: `1px solid ${appTextColor}40`,
                      color: appTextColor,
                    }}
                  >
                    <Trans
                      i18nKey="auth:emailMismatchOverride"
                      defaults="No, use the new email"
                    />
                  </button>
                  <button
                    type="button"
                    disabled={emailSignUpLoading}
                    onClick={() => setEmailMismatch(null)}
                    className="flex min-h-[44px] items-center justify-center text-sm font-medium disabled:opacity-50"
                    style={{ color: `${appTextColor}99` }}
                  >
                    <Trans i18nKey="common:cancel" defaults="Cancel" />
                  </button>
                </div>
              </div>
            )}

            {/* Phone-mismatch resolution banner (parity with web). EMAIL matched
                a guest whose on-file phone differs from the typed one; keep the
                (often WhatsApp-proven) number on file or use the new one. */}
            {phoneMismatch && (
              <div
                className="rounded-lg p-4 text-sm"
                style={{
                  backgroundColor: `${appTextColor}10`,
                  border: `1px solid ${appTextColor}40`,
                  color: appTextColor,
                }}
              >
                <p className="font-medium">
                  <Trans i18nKey="auth:phoneMismatchHeading" defaults="Is this you?" />
                </p>
                <p className="mt-1" style={{ color: `${appTextColor}99` }}>
                  <Trans
                    i18nKey="auth:phoneMismatchBody"
                    values={{
                      phoneHint: phoneMismatch.phoneHint,
                      typedPhone: phoneMismatch.values.phoneNumber ?? '',
                    }}
                    components={{ strong: <strong style={{ color: appTextColor }} /> }}
                    defaults="We already have <strong>{{phoneHint}}</strong> on file for this account. You typed <strong>{{typedPhone}}</strong>. Which number do you want to keep?"
                  />
                </p>
                <div className="mt-3 flex flex-col gap-2">
                  <NativeAuthButton
                    type="button"
                    disabled={emailSignUpLoading}
                    loading={emailSignUpLoading}
                    primaryColor={primaryColor}
                    secondaryColor={secondaryColor}
                    appTextColor={appTextColor}
                    onClick={() => resolvePhoneMismatch('use-existing')}
                  >
                    <Trans
                      i18nKey="auth:phoneMismatchUseExisting"
                      defaults="Keep the number on file"
                    />
                  </NativeAuthButton>
                  <button
                    type="button"
                    disabled={emailSignUpLoading}
                    onClick={() => resolvePhoneMismatch('override')}
                    className="flex min-h-[44px] items-center justify-center rounded-xl px-4 text-sm font-medium disabled:opacity-50"
                    style={{
                      border: `1px solid ${appTextColor}40`,
                      color: appTextColor,
                    }}
                  >
                    <Trans
                      i18nKey="auth:phoneMismatchOverride"
                      defaults="No, use the new number"
                    />
                  </button>
                  <button
                    type="button"
                    disabled={emailSignUpLoading}
                    onClick={() => setPhoneMismatch(null)}
                    className="flex min-h-[44px] items-center justify-center text-sm font-medium disabled:opacity-50"
                    style={{ color: `${appTextColor}99` }}
                  >
                    <Trans i18nKey="common:cancel" defaults="Cancel" />
                  </button>
                </div>
              </div>
            )}

            {/* Guest-upgrade welcome banner — only shown alongside the email form */}
            {prefillPhoneHint && (
              <div
                className="rounded-lg p-3 text-sm"
                style={{
                  backgroundColor: `${appTextColor}10`,
                  border: `1px solid ${appTextColor}40`,
                  color: appTextColor,
                }}
              >
                <p className="font-medium">
                  {prefillFirstName ? (
                    <Trans
                      i18nKey="auth:welcomeBackNamed"
                      values={{ name: prefillFirstName }}
                      defaults="Welcome back, {{name}}"
                    />
                  ) : (
                    <Trans i18nKey="auth:welcomeBack" defaults="Welcome back" />
                  )}
                </p>
                <p className="mt-1" style={{ color: `${appTextColor}99` }}>
                  <Trans
                    i18nKey="auth:welcomeBackBody"
                    values={{ phoneHint: prefillPhoneHint }}
                    components={{
                      strong: <strong style={{ color: appTextColor }} />,
                    }}
                    defaults="We have a profile linked to <strong>{{phoneHint}}</strong>. Confirm your phone number and set a password to finish setting up your account."
                  />
                </p>
              </div>
            )}

            <Form {...emailForm}>
            <form
              onSubmit={emailForm.handleSubmit((data) => handleEmailSignUp(data))}
              className="flex flex-col gap-5"
            >
              <NativeGroupedInput appBackgroundColor={appBackgroundColor}>
                <FormField
                  control={emailForm.control}
                  name="name"
                  render={({ field, fieldState }) => {
                    const showSingleWordHint =
                      prefillNeedsFullName &&
                      Boolean(field.value) &&
                      !isFullName(field.value) &&
                      !fieldState.error;
                    return (
                      <FormItem>
                        <FormControl>
                          <NativeInputRow appTextColor={appTextColor}>
                            <input
                              type="text"
                              autoComplete="name"
                              placeholder={t('auth:namePlaceholder')}
                              disabled={isLoading}
                              className={inputClass}
                              data-test="native-email-name-input"
                              {...field}
                            />
                          </NativeInputRow>
                        </FormControl>
                        <FormMessage className="px-1" />
                        {showSingleWordHint && (
                          <p className="text-destructive px-1 text-xs">
                            <Trans i18nKey="auth:nameMustBeFullName" />
                          </p>
                        )}
                      </FormItem>
                    );
                  }}
                />
                <FormField
                  control={emailForm.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <NativeInputRow appTextColor={appTextColor}>
                          <input
                            type="email"
                            autoComplete="email"
                            placeholder="Email"
                            disabled={isLoading}
                            className={inputClass}
                            data-test="native-email-signup-email-input"
                            {...field}
                          />
                        </NativeInputRow>
                      </FormControl>
                      <FormMessage className="px-1 text-red-400" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={emailForm.control}
                  name="password"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <NativeInputRow appTextColor={appTextColor}>
                          <input
                            type="password"
                            autoComplete="new-password"
                            placeholder={t('auth:passwordPlaceholder', 'Password')}
                            disabled={isLoading}
                            className={inputClass}
                            data-test="native-email-signup-password-input"
                            {...field}
                          />
                        </NativeInputRow>
                      </FormControl>
                      <FormMessage className="px-1 text-red-400" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={emailForm.control}
                  name="repeatPassword"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <NativeInputRow appTextColor={appTextColor}>
                          <input
                            type="password"
                            autoComplete="new-password"
                            placeholder={t('auth:repeatPasswordPlaceholder', 'Repeat Password')}
                            disabled={isLoading}
                            className={inputClass}
                            data-test="native-email-signup-repeat-password-input"
                            {...field}
                          />
                        </NativeInputRow>
                      </FormControl>
                      <FormMessage className="px-1 text-red-400" />
                    </FormItem>
                  )}
                />
                <FormField
                  control={emailForm.control}
                  name="phoneNumber"
                  render={({ field }) => (
                    <FormItem>
                      <FormControl>
                        <NativeInputRow appTextColor={appTextColor}>
                          <PhoneInput
                            value={field.value}
                            onChange={field.onChange}
                            onBlur={field.onBlur}
                            disabled={isLoading}
                            autoComplete="tel"
                            placeholder={t('common:phoneNumber', 'Phone number')}
                            className={`native-phone-input border-0 bg-transparent text-base shadow-none focus-visible:ring-0 ${isDark ? 'text-white placeholder:text-white/40' : 'text-gray-900 placeholder:text-gray-400'}`}
                            data-test="native-email-signup-phone-input"
                          />
                        </NativeInputRow>
                      </FormControl>
                      <FormMessage className="px-1 text-red-400" />
                    </FormItem>
                  )}
                />
                {signupCustomFields.some((field) => field.for_parent) && (
                  <div className="space-y-3 p-3">
                    <SignupCustomFieldsInput
                      fields={signupCustomFields}
                      disabled={isLoading}
                    />
                  </div>
                )}
              </NativeGroupedInput>

              {displayChildrenSection && (
                <ChildrenSection
                  fields={emailChildren.fields}
                  append={emailChildren.append}
                  remove={emailChildren.remove}
                  control={emailForm.control}
                  isOpen={childrenOpenEmail}
                  setIsOpen={setChildrenOpenEmail}
                  primaryColor={primaryColor}
                  appTextColor={appTextColor}
                  isDark={isDark}
                  childInputClass={childInputClass}
                />
              )}

              {captcha.field}

              <NativeAuthButton
                type="submit"
                disabled={isLoading || !emailForm.formState.isValid}
                loading={emailSignUpLoading}
                primaryColor={primaryColor}
                secondaryColor={secondaryColor}
                appTextColor={appTextColor}
              >
                <Trans i18nKey="auth:signUpHeading" />
              </NativeAuthButton>

              {hasBothMethods && (
                <button
                  type="button"
                  onClick={() => {
                    setEmailMismatch(null);
                    setMode('phone');
                  }}
                  className="flex min-h-[44px] items-center justify-center text-sm font-medium"
                  style={{ color: appTextColor }}
                >
                  <Trans i18nKey="auth:usePhoneInstead" />
                </button>
              )}

              <TermsInlineNotice
                display={displayTermsCheckbox}
                appTextColor={appTextColor}
              />
            </form>
          </Form>
          </>
        )}

        {/* Existing users go here */}
        <div className="flex justify-center">
          <Link
            href={signInPath}
            className="flex min-h-[44px] items-center text-sm font-medium"
            style={{ color: appTextColor }}
          >
            <Trans i18nKey="auth:signIn" />
          </Link>
        </div>
      </div>

      {/* Global native phone input overrides */}
      <style
        dangerouslySetInnerHTML={{
          __html: `
            .native-phone-input button[type="button"] {
              background: transparent !important;
              border: none !important;
              box-shadow: none !important;
              color: ${appTextColor} !important;
            }
            .native-phone-input input {
              background: transparent !important;
              border: none !important;
              box-shadow: none !important;
              color: ${appTextColor} !important;
            }
            .native-phone-input input::placeholder {
              color: ${appTextColor}66 !important;
            }
          `,
        }}
      />
    </NativeAuthShell>
  );
}

// --- Extracted shared components ---

// Control and fields are typed as the shared ChildEntry shape. We use Control<{children: ChildEntry[]}>
// rather than the full form type to keep this component reusable across phone and email forms.
// The eslint-disable comments below are only needed because react-hook-form's Control is
// not easily narrowed to a partial schema — this is an accepted limitation of the pattern.
function ChildrenSection({
  fields,
  append,
  remove,
  control,
  isOpen,
  setIsOpen,
  primaryColor,
  appTextColor,
  isDark,
  childInputClass,
}: {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  fields: any[];
  append: (value: ChildEntry) => void;
  remove: (index: number) => void;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  control: any;
  isOpen: boolean;
  setIsOpen: (open: boolean) => void;
  primaryColor: string;
  appTextColor: string;
  isDark: boolean;
  childInputClass: string;
}) {
  return (
    <div>
      <button
        type="button"
        onClick={() => {
          if (!isOpen) {
            setIsOpen(true);
            if (fields.length === 0) {
              append({ firstName: '', lastName: '' });
            }
          } else {
            setIsOpen(false);
          }
        }}
        className="flex min-h-[44px] w-full items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors"
        style={{
          backgroundColor: isOpen
            ? `${primaryColor}15`
            : isDark
              ? 'rgba(255,255,255,0.06)'
              : 'rgba(0,0,0,0.04)',
        }}
      >
        <Plus
          className="h-4 w-4 transition-transform"
          style={{
            color: appTextColor,
            transform: isOpen ? 'rotate(45deg)' : undefined,
          }}
        />
        <span
          className="flex-1 text-sm font-medium"
          style={{ color: appTextColor }}
        >
          <Trans i18nKey="auth:registeringChildren" />
        </span>
        <ChevronDown
          className="h-4 w-4 transition-transform"
          style={{
            color: `${appTextColor}80`,
            transform: isOpen ? 'rotate(180deg)' : undefined,
          }}
        />
      </button>

      {isOpen && (
        <div
          className="mt-1 space-y-2 rounded-xl p-3"
          style={{ backgroundColor: `${primaryColor}10` }}
        >
          <p className="text-xs" style={{ color: `${appTextColor}80` }}>
            <Trans i18nKey="auth:childrenHelperText" />
          </p>

          {fields.map((field: { id: string }, index: number) => (
            <div key={field.id} className="flex items-center gap-2">
              <span
                className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full text-xs font-medium"
                style={{
                  backgroundColor: `${appTextColor}20`,
                  color: appTextColor,
                }}
              >
                {index + 1}
              </span>
              <FormField
                control={control}
                name={`children.${index}.firstName`}
                render={({ field: f }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <input
                        placeholder="First name"
                        className={childInputClass}
                        data-test={`native-child-first-${index}`}
                        {...f}
                      />
                    </FormControl>
                    <FormMessage className="px-1 text-red-400" />
                  </FormItem>
                )}
              />
              <FormField
                control={control}
                name={`children.${index}.lastName`}
                render={({ field: f }) => (
                  <FormItem className="flex-1">
                    <FormControl>
                      <input
                        placeholder="Last name"
                        className={childInputClass}
                        data-test={`native-child-last-${index}`}
                        {...f}
                      />
                    </FormControl>
                    <FormMessage className="px-1 text-red-400" />
                  </FormItem>
                )}
              />
              <button
                type="button"
                onClick={() => {
                  remove(index);
                  if (fields.length <= 1) setIsOpen(false);
                }}
                className="shrink-0 rounded-md p-1.5 active:opacity-70"
              >
                <X
                  className="h-3.5 w-3.5"
                  style={{ color: `${appTextColor}60` }}
                />
              </button>
            </div>
          ))}

          {fields.length < MAX_CHILDREN && (
            <button
              type="button"
              onClick={() => append({ firstName: '', lastName: '' })}
              className="flex items-center gap-1.5 text-sm font-medium"
              style={{ color: appTextColor }}
            >
              <Plus className="h-3.5 w-3.5" />
              <Trans i18nKey="auth:addAnotherChild" />
            </button>
          )}
        </div>
      )}
    </div>
  );
}

function TermsInlineNotice({
  display,
  appTextColor,
}: {
  display?: boolean;
  appTextColor: string;
}) {
  if (!display) return null;

  return (
    <p
      className="px-2 text-center text-xs"
      style={{ color: `${appTextColor}99` }}
    >
      <Trans
        i18nKey="auth:byCreatingAccountAgreeToTerms"
        components={{
          TermsOfServiceLink: (
            <a
              href="/terms-of-service"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: appTextColor }}
            >
              <Trans i18nKey="auth:termsOfService" />
            </a>
          ),
          PrivacyPolicyLink: (
            <a
              href="/privacy-policy"
              target="_blank"
              rel="noopener noreferrer"
              className="underline"
              style={{ color: appTextColor }}
            >
              <Trans i18nKey="auth:privacyPolicy" />
            </a>
          ),
        }}
      />
    </p>
  );
}

function isDarkColor(hex: string): boolean {
  const c = hex.replace('#', '');
  const r = parseInt(c.substring(0, 2), 16);
  const g = parseInt(c.substring(2, 4), 16);
  const b = parseInt(c.substring(4, 6), 16);
  return (0.299 * r + 0.587 * g + 0.114 * b) / 255 < 0.5;
}
